import { useEffect, useRef, useState } from 'react';
import { FaWhatsapp } from 'react-icons/fa';
import styled, { keyframes } from 'styled-components';
import branchData from '../ContactUs/contactData';

const pulse = keyframes`
    0% { box-shadow: 0 0 0 0 rgba(37, 211, 102, 0.5); }
    70% { box-shadow: 0 0 0 30px rgba(37, 211, 102, 0); }
    100% { box-shadow: 0 0 0 0 rgba(37, 211, 102, 0); }
`;

const slideUp = keyframes`
    from {
        opacity: 0;
        transform: translateY(8px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
`;

const WhatsAppWrapper = styled.div`
    position: fixed;
    bottom: 30px;
    right: 30px;
    z-index: 11111;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
`;

const PopupPanel = styled.div`
    margin-bottom: 12px;
    width: 260px;
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
    overflow: hidden;
    animation: ${slideUp} 0.2s ease-out;

    @media (max-width: 400px) {
        width: 220px;
    }

    @media (min-width: 768px) {
        width: 420px;
    }
`;

const PopupHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
    background: #25d366;
    color: #fff;

    span {
        font-size: 14px;
        font-weight: 600;
    }

    button {
        background: none;
        border: none;
        color: #fff;
        font-size: 18px;
        line-height: 1;
        cursor: pointer;
        padding: 0;
        opacity: 0.85;

        &:hover {
            opacity: 1;
        }
    }

    @media (min-width: 768px) {
        padding: 16px 16px;

        span {
            font-size: 20px;
            font-weight: 600;
        }

        button {
            font-size: 24px;
        }
    }
`;

const BranchItem = styled.a`
    display: flex;
    flex-direction: column;
    padding: 10px 16px;
    text-decoration: none;
    border-left: 3px solid transparent;
    transition: background 0.15s, border-color 0.15s;

    &:not(:last-child) {
        border-bottom: 1px solid #f0f0f0;
    }

    &:hover {
        background: #f6fff9;
        border-left-color: #25d366;
    }

    .branch-name {
        font-size: 13px;
        font-weight: 600;
        color: #1a1a1a;
    }

    .branch-number {
        font-size: 12px;
        color: #555;
        margin-top: 2px;
    }

    @media (min-width: 768px) {
        .branch-name {
            font-size: 20px;
            font-weight: 500;
            color: #1a1a1a;
        }

        .branch-number {
            font-size: 16px;
            color: #555;
            margin-top: 2px;
        }
    }
`;

const CTAButton = styled.button`
    width: 120px;
    height: 120px;
    background-color: #25d366;
    color: #fff;
    border: none;
    border-radius: 50%;
    font-size: 80px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    animation: ${pulse} 1s infinite;
    transition: transform 0.3s, box-shadow 0.3s;

    &:hover {
        transform: scale(1.1);
        box-shadow: 0 4px 16px rgba(37, 211, 102, 0.5);
    }

    @media (max-width: 400px) {
        width: 80px;
        height: 80px;
        font-size: 48px;
    }
`;

function getWhatsAppUrl(whatsappField) {
    const digits = whatsappField.split('/')[0].replace(/[^0-9]/g, '');
    return `https://wa.me/${digits}`;
}

function getDisplayNumber(whatsappField) {
    return whatsappField.split('/')[0].trim();
}

function WhatsAppCTA() {
    const [isOpen, setIsOpen] = useState(false);
    const wrapperRef = useRef(null);

    useEffect(() => {
        function handleClickOutside(e) {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setIsOpen(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
        <WhatsAppWrapper ref={wrapperRef}>
            {isOpen && (
                <PopupPanel>
                    <PopupHeader>
                        <span>Chat with us</span>
                        <button type="button" onClick={() => setIsOpen(false)} aria-label="Close">
                            ×
                        </button>
                    </PopupHeader>
                    {branchData.map((branch) => (
                        <BranchItem
                            key={branch.id}
                            href={getWhatsAppUrl(branch.whatsapp)}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => setIsOpen(false)}
                        >
                            <span className="branch-name">{branch.name}</span>
                            <span className="branch-number">{getDisplayNumber(branch.whatsapp)}</span>
                        </BranchItem>
                    ))}
                </PopupPanel>
            )}
            <CTAButton
                type="button"
                onClick={() => setIsOpen((prev) => !prev)}
                aria-label="Contact us on WhatsApp"
            >
                <FaWhatsapp />
            </CTAButton>
        </WhatsAppWrapper>
    );
}

export default WhatsAppCTA;
